import React from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Minus, Plus, Info } from 'lucide-react';

export const VISA_TYPES = {
  ETA: { name: '2-Year Multiple Entry ETA', hint: 'UK passport holders only (all UK passport types) · valid 730 days · multiple entry · up to 180 days total stay · not valid for Hajj' },
  MULTIPLE: { name: '1-Year Multiple Entry Visa', hint: 'Standard multiple-entry tourist visa valid for Umrah' },
  UMRAH: { name: 'Umrah Visa', hint: 'Issued via the Saudi Nusuk platform' },
};
export const NATIONALITIES = [
  { key: 'british', flag: '🇬🇧', label: 'British Passport', hint: '2-Year ETA / 1-Year Multiple Entry / Umrah Visa', allowed: ['ETA', 'MULTIPLE', 'UMRAH'], active: 'border-green-300 bg-green-50' },
  { key: 'eu', flag: '🇪🇺', label: 'EU Passport', hint: '1-Year Multiple Entry / Umrah Visa', warn: '2-Year ETA not available (UK passports only)', allowed: ['MULTIPLE', 'UMRAH'], active: 'border-blue-300 bg-blue-50' },
  { key: 'other', flag: '🌍', label: 'Other Nationality', hint: 'Umrah Visa', allowed: ['UMRAH'], active: 'border-amber-300 bg-amber-50' },
];
export const defaultVisa = (total) => ({ british: { count: total, type: 'ETA' }, eu: { count: 0, type: 'MULTIPLE' }, other: { count: 0, type: 'UMRAH' } });
export const visaAssigned = (v) => NATIONALITIES.reduce((n, { key }) => n + (v[key]?.count || 0), 0);
// Clamp the breakdown to the group size; if everyone needed a visa before, keep it that way as travellers are added
export const fitVisaToTotal = (v, total, prevTotal) => {
  const eu = Math.min(v.eu?.count || 0, total);
  const other = Math.min(v.other?.count || 0, total - eu);
  const allAssigned = prevTotal !== undefined && visaAssigned(v) === prevTotal;
  const british = allAssigned ? total - eu - other : Math.min(v.british?.count || 0, total - eu - other);
  return { ...v, british: { ...v.british, count: british }, eu: { ...v.eu, count: eu }, other: { ...v.other, count: other } };
};

const Counter = ({ nat, value, canAdd, onChange }) => (
  <div className={`p-3 rounded-lg border transition-all ${value > 0 ? nat.active : 'border-slate-200 bg-white'}`} data-testid={`visa-nat-${nat.key}`}>
    <div className="flex items-center justify-between gap-2">
      <div>
        <div className="font-medium text-sm">{nat.flag} {nat.label}</div>
        <div className="text-xs text-slate-500">{nat.hint}</div>
        {nat.warn && <div className="text-xs text-red-500">{nat.warn}</div>}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onChange(value - 1)} disabled={value === 0} data-testid={`visa-nat-${nat.key}-minus`}><Minus className="h-4 w-4" /></Button>
        <span className="w-8 text-center font-bold text-lg" data-testid={`visa-nat-${nat.key}-value`}>{value}</span>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onChange(value + 1)} disabled={!canAdd} data-testid={`visa-nat-${nat.key}-plus`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  </div>
);

const TypeChooser = ({ nat, value, count, onChange }) => (
  <div className="space-y-2 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`visa-type-${nat.key}`}>
    <Label className="text-sm font-medium flex items-center gap-2"><span className="text-lg">{nat.flag}</span>{nat.label} Visa Type ({count} passenger{count > 1 ? 's' : ''})</Label>
    {nat.key === 'eu' && <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">⚠️ The 2-Year Multiple Entry ETA is <strong>UK passport holders only</strong> — not available for EU passports</div>}
    {nat.allowed.length === 1 ? (
      <div className="p-3 rounded-lg border bg-green-50 border-green-200"><div className="font-medium">{VISA_TYPES.UMRAH.name}</div><div className="text-xs font-medium text-emerald-700">Included in package price</div><div className="text-xs text-slate-600 mt-1">Only the Umrah Visa is available for other nationalities</div></div>
    ) : (
      <RadioGroup value={value} onValueChange={onChange}>
        <div className={`grid grid-cols-1 gap-2 ${nat.allowed.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
          {nat.allowed.map((code) => (
            <div key={code} className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer bg-white ${value === code ? 'border-[#FF6600] bg-orange-50' : 'hover:bg-slate-50'}`}>
              <RadioGroupItem value={code} id={`${nat.key}-visa-${code}`} data-testid={`visa-type-${nat.key}-${code}`} />
              <Label htmlFor={`${nat.key}-visa-${code}`} className="cursor-pointer flex-1">
                <div className="font-medium">{VISA_TYPES[code].name}</div>
                <div className="text-xs font-medium text-emerald-700">Included in package price</div>
                <div className="text-[11px] text-slate-500 leading-snug mt-1">{VISA_TYPES[code].hint}</div>
              </Label>
            </div>
          ))}
        </div>
      </RadioGroup>
    )}
  </div>
);

export const VisaPicker = ({ value, total, onChange }) => {
  const assigned = visaAssigned(value);
  const own = total - assigned;
  const set = (key, patch) => onChange({ ...value, [key]: { ...value[key], ...patch } });
  return (
    <div className="space-y-3" data-testid="pkg-visa-picker">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Passenger Passport Nationalities</Label>
        <Badge className={`text-xs ${own === 0 ? 'bg-green-100 text-green-700' : 'bg-sky-100 text-sky-700'}`} data-testid="visa-assigned-badge">{assigned} / {total} need a visa</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {NATIONALITIES.map((nat) => <Counter key={nat.key} nat={nat} value={value[nat.key]?.count || 0} canAdd={assigned < total} onChange={(n) => set(nat.key, { count: n })} />)}
        <div className="p-3 rounded-lg border border-red-200 bg-red-50" data-testid="visa-nat-travel-document">
          <div className="flex items-center justify-between">
            <div><div className="font-medium text-sm text-red-700">📄 Travel Document</div><div className="text-xs text-red-500 font-semibold">NOT ALLOWED</div></div>
            <div className="flex items-center gap-2 opacity-40"><Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled><Minus className="h-4 w-4" /></Button><span className="w-8 text-center font-bold text-lg text-red-400">0</span><Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled><Plus className="h-4 w-4" /></Button></div>
          </div>
        </div>
      </div>
      {own > 0 && (
        <div className="flex items-center gap-2 p-2 bg-sky-50 rounded-lg border border-sky-200" data-testid="visa-own-note">
          <Info className="h-4 w-4 text-sky-600" />
          <span className="text-sm text-sky-800">{own} traveller{own !== 1 ? 's' : ''} already hold{own === 1 ? 's' : ''} a valid visa — no visa will be added for {own === 1 ? 'them' : 'these travellers'}.{assigned === 0 ? ' No visa is included in this package.' : ''}</span>
        </div>
      )}
      {NATIONALITIES.filter((nat) => (value[nat.key]?.count || 0) > 0).map((nat) => <TypeChooser key={nat.key} nat={nat} count={value[nat.key].count} value={value[nat.key].type} onChange={(t) => set(nat.key, { type: t })} />)}
      {assigned > 0 && (
        <div className="p-3 bg-blue-100 rounded-lg space-y-1" data-testid="visa-summary">
          <div className="text-sm font-medium text-blue-800 mb-1">Visa Summary</div>
          {NATIONALITIES.filter((nat) => value[nat.key].count > 0).map((nat) => (
            <div key={nat.key} className="flex items-center justify-between text-sm"><span className="text-blue-700">{nat.flag} {nat.label.replace(' Passport', '')} - {VISA_TYPES[value[nat.key].type].name} ({value[nat.key].count} pax)</span><span className="font-medium text-blue-800">Included</span></div>
          ))}
          {own > 0 && <div className="flex items-center justify-between text-sm"><span className="text-blue-700">Own valid visa ({own} pax)</span><span className="font-medium text-blue-800">Not needed</span></div>}
        </div>
      )}
    </div>
  );
};
